import api from './api';

/**
 * #359: the insurer's Commission Report. Commission is what the insurer owes
 * under its OWN published schedules; Paid is what the insurer recorded
 * (settlements), never broker payouts. Amounts are decimal strings.
 */

export type ReportStatus = 'calculated' | 'approved' | 'part_paid' | 'paid' | 'no_rule';

export const REPORT_STATUS_LABEL: Record<ReportStatus, string> = {
  calculated: 'Calculated',
  approved: 'Approved',
  part_paid: 'Part-paid',
  paid: 'Paid',
  no_rule: 'No rule',
};

export interface ReportRow {
  broker_id: string;
  broker_name: string;
  policy_id: string;
  policy_number: string;
  /** The only holder field an insurer sees (#443). */
  holder_name: string;
  product: string;
  month: string;
  premium: string;
  /** null = no published rule covers this policy ("Not in a schedule"). */
  commission: string | null;
  status: ReportStatus;
  paid: string;
  pending: string | null;
}

export interface ReportKpis {
  calculated: string;
  approved: string;
  paid: string;
  pending: string;
  on_account: string;
  no_rule_policies: number;
}

export interface ReportFilters {
  broker?: string;
  product?: string;
  month?: string;
  date_from?: string;
  date_to?: string;
  policy?: string;
  status?: ReportStatus | '';
  page?: number;
}

export interface ReportResponse {
  kpis: ReportKpis;
  count: number;
  page: number;
  page_size: number;
  results: ReportRow[];
}

const clean = (f: ReportFilters = {}) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

export const insurerReportService = {
  async get(filters?: ReportFilters): Promise<ReportResponse> {
    return (await api.get('/commissions/insurer-report/', { params: clean(filters) })).data;
  },
  async exportCsv(filters?: ReportFilters): Promise<Blob> {
    return (await api.get('/commissions/insurer-report/export/', { params: clean(filters), responseType: 'blob' })).data;
  },
  async brokers(): Promise<{ id: string; name: string }[]> {
    const res = await api.get('/commissions/insurer-schedules/eligible-brokers/');
    return res.data.results ?? [];
  },
  async products(): Promise<{ id: string; name: string }[]> {
    const res = await api.get('/insurers/insurance/policies/', { params: { page_size: 200 } });
    const list = Array.isArray(res.data) ? res.data : (res.data.results ?? []);
    return list.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name }));
  },
};
